import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  Copy,
  LogOut,
  Building2,
  ShieldCheck,
  Phone,
  Crown,
  Lock,
  UserCheck,
} from 'lucide-react';
import { useCurrentUser } from '../../application/hooks/useCurrentUser';
import { useLogout } from '../../application/hooks/useAuth';
import { useTranslation } from '../../application/hooks/useTranslation';
import { QueryState } from '../components/feedback/QueryState';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { TierLevel, TIER_DESIGNATION_MAP } from '../../domain/enums/TierLevel';
import { KycDetailsCard } from '../components/profile/KycDetailsCard';
import { NomineeCard } from '../components/profile/NomineeCard';
import { LinkedPaymentCard } from '../components/profile/LinkedPaymentCard';
import { ChangePasswordCard } from '../components/profile/ChangePasswordCard';
import { ProfileAvatar } from '../components/profile/ProfileAvatar';
import { initials as nameInitials } from './team/teamFormat';
import './pages.css';
import './Profile.css';

/**
 * World-Class Owner Profile View (W04).
 * Combines exclusive black & gold owner card with bank-grade KYC status and succession details.
 */
export default function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isPending, error, refetch } = useCurrentUser();
  const logout = useLogout();
  const [copied, setCopied] = useState(false);

  const copyId = async () => {
    if (!user) return;
    try {
      await navigator.clipboard.writeText(user.apartmentId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — id stays visible on screen */
    }
  };

  const initials = user?.fullName?.trim() ? nameInitials(user.fullName) : 'SG';

  const isL1 = user?.tier === TierLevel.L1;
  const tierTitle = user ? TIER_DESIGNATION_MAP[user.tier] ?? user.tier : 'Owner';
  // PRD W04 §4.2: L1–L5 without a sponsor get an advisory; Directors (L6) have none by design.
  const needsSponsor = !!user && user.tier !== TierLevel.L6 && user.tier !== TierLevel.ADMIN && !user.parentId;

  return (
    <div data-testid="profile-page">
      <div className="page-head">
        <h1 className="page-title">{t('profile.title')}</h1>
        <Badge tone="neutral">{t('profile.readOnly')}</Badge>
      </div>

      <QueryState loading={isPending} error={error} onRetry={() => void refetch()} skeletonCount={2} />

      {user && (
        <div className="stack profile-page-container">
          {/* 1. Owner Profile Card (Black & Gold / Emerald Luxury Card) */}
          <div className="profile-passport-card">
            <div className="profile-passport-inner">
              <div className="profile-passport-top">
                <div className="profile-passport-brand">
                  <span className="profile-passport-kicker">
                    <Building2 size={13} color="#fed766" /> Sristi Green Valley Ltd
                  </span>
                  <span className="profile-passport-org">{isL1 ? t('profile.passportL1') : t('profile.passportAgent')}</span>
                </div>

                <div className="profile-passport-tier-pill">
                  <Crown size={14} color="#fed766" />
                  <span>{tierTitle}</span>
                </div>
              </div>

              <div className="profile-passport-middle">
                <div className="profile-user-group">
                  <ProfileAvatar user={user} initials={initials} />
                  <div className="profile-user-titles">
                    <h2 className="profile-user-name">{user.fullName}</h2>
                    <span className="profile-user-phone">
                      <Phone size={13} /> {user.phone}
                    </span>
                  </div>
                </div>

                <div className="profile-account-id-box">
                  <span className="profile-account-id-label">{isL1 ? t('profile.accountIdLabelL1') : t('profile.accountIdLabelAgent')}</span>
                  <div className="profile-id-chip">
                    <span>{user.apartmentId}</span>
                    <button
                      className="profile-copy-btn"
                      onClick={() => void copyId()}
                      aria-label={t('profile.copyId')}
                      data-testid="copy-id"
                      title="Copy Account ID"
                    >
                      {copied ? <Check size={16} color="#10b981" /> : <Copy size={16} />}
                    </button>
                  </div>
                  {copied && (
                    <span role="status" style={{ fontSize: '0.6875rem', color: '#a7f3d0', fontWeight: 600 }}>
                      ✓ {t('profile.copied')}
                    </span>
                  )}
                </div>
              </div>

              <div className="profile-passport-bottom">
                <div className="profile-security-badge">
                  <ShieldCheck size={15} color="#10b981" />
                  <span>Central Corporate Escrow Registry</span>
                </div>
                {isL1 && <span style={{ opacity: 0.85 }}>SGVL Residential Tenure</span>}
              </div>
            </div>
          </div>

          {/* 2. Account Details Section */}
          <section className="profile-section" aria-labelledby="profile-account-heading">
            <div className="profile-section-header">
              <h2 id="profile-account-heading" className="profile-section-title">
                {t('profile.sectionAccountTitle')}
              </h2>
              <p className="profile-section-subtitle">
                {t('profile.sectionAccountSubtitle')}
              </p>
            </div>

            <div className="profile-detail-grid">
              {/* Primary Identity / Overview Card */}
              <section className="profile-card" data-testid="profile-overview-card">
                <div className="profile-card-header">
                  <div className="profile-card-header-main">
                    <div className="profile-card-icon-tile profile-icon-emerald">
                      <UserCheck size={18} aria-hidden />
                    </div>
                    <div className="profile-card-title-wrap">
                      <h3 className="profile-card-title">{user.fullName}</h3>
                      <p className="profile-card-subtitle">{t('profile.accountOverviewSubtitle')}</p>
                    </div>
                  </div>
                  <div className="profile-card-header-badge">
                    <Badge tone={isL1 ? 'success' : 'accent'}>
                      {TIER_DESIGNATION_MAP[user.tier] ?? user.tier}
                    </Badge>
                  </div>
                </div>

                <div className="profile-card-body">
                  <dl className="profile-kv-grid kv">
                    <div className="profile-kv-item">
                      <dt className="profile-kv-label">{t('profile.accountId')}</dt>
                      <dd className="profile-kv-value tabular row" style={{ gap: 8, alignItems: 'center' }}>
                        <span>{user.apartmentId}</span>
                        <button
                          className="tap profile-copy-inline-btn"
                          onClick={() => void copyId()}
                          aria-label={t('profile.copyId')}
                          type="button"
                          title="Copy Account ID"
                        >
                          {copied ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
                        </button>
                        {copied && (
                          <span className="muted" role="status" style={{ fontSize: '0.75rem', color: '#047857', fontWeight: 600 }}>
                            {t('profile.copied')}
                          </span>
                        )}
                      </dd>
                    </div>

                    <div className="profile-kv-item">
                      <dt className="profile-kv-label">{t('auth.phone')}</dt>
                      <dd className="profile-kv-value tabular">{user.phone}</dd>
                    </div>

                    <div className="profile-kv-item">
                      <dt className="profile-kv-label">{isL1 ? t('profile.accountType') : t('profile.designation')}</dt>
                      <dd className="profile-kv-value">
                        <span style={{ fontWeight: 600 }}>{TIER_DESIGNATION_MAP[user.tier] ?? user.tier}</span>
                      </dd>
                    </div>

                    {!isL1 && (
                      <div className="profile-kv-item profile-kv-full">
                        <dt className="profile-kv-label">{t('profile.sponsor')}</dt>
                        <dd className="profile-kv-value">
                          {user.sponsor ? (
                            <>
                              <span style={{ fontWeight: 600 }}>{user.sponsor.fullName}</span>
                              <span className="muted tabular"> ({user.sponsor.userId})</span>
                              <span className="muted tabular" data-testid="sponsor-phone"> · {user.sponsor.phoneMasked}</span>
                            </>
                          ) : user.parentId ? (
                            <>
                              <span style={{ fontWeight: 600 }}>{user.parentName ?? user.parentId}</span>
                              {user.parentName && <span className="muted tabular"> ({user.parentId})</span>}
                            </>
                          ) : needsSponsor ? (
                            <span role="alert" data-testid="sponsor-missing" style={{ color: '#b45309', fontWeight: 600 }}>
                              {t('profile.noSponsorAdvisory')}
                            </span>
                          ) : (
                            t('profile.noSponsorDirector')
                          )}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </section>

              <KycDetailsCard user={user} />
              {/* Nominee: L1 only (owner decision) — the card also guards by tier */}
              {isL1 && <NomineeCard user={user} />}
            </div>
          </section>

          {/* 3. Security & Settlement Section */}
          <section className="profile-section" aria-labelledby="profile-security-heading">
            <div className="profile-section-header">
              <h2 id="profile-security-heading" className="profile-section-title">
                {t('profile.sectionSecurityTitle')}
              </h2>
              <p className="profile-section-subtitle">
                {t('profile.sectionSecuritySubtitle')}
              </p>
            </div>

            <div className="profile-detail-grid">
              <LinkedPaymentCard payout={user.payout} />
              <ChangePasswordCard phone={user.phone} />
            </div>
          </section>

          {/* 3. Security & Sign Out Strip */}
          <div className="profile-action-card">
            <div className="profile-action-left">
              <div className="profile-action-icon-wrap">
                <Lock size={18} />
              </div>
              <div>
                <strong style={{ fontSize: '0.84375rem', color: '#14251c', display: 'block' }}>
                  Bank-Grade KYC & Security Session
                </strong>
                <span className="muted" style={{ fontSize: '0.75rem' }}>
                  Authenticated with dual-token rotation and encrypted credentials
                </span>
              </div>
            </div>

            <Button
              variant="danger"
              data-testid="profile-logout"
              loading={logout.isPending}
              onClick={() => logout.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) })}
            >
              <LogOut size={16} /> {t('nav.logout')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
